<script setup lang="ts">
  import { Checkbox, CheckboxSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { DESTINATION_TEXTS } from '../config/texts';
  import { formatMinPrice } from '../lib/formatMinPrice';
  import { splitHotelName, type HotelNameParts } from '../lib/splitHotelName';
  import { getVisaLabel } from '../lib/visaLabel';
  import type { Country, Hotel, Resort, DestinationKind } from '@/shared/model';

  type DestinationOptionKind = `${DestinationKind}` | 'selectAll';

  interface DestinationOptionProps {
    option: Country | Resort | Hotel;
    kind: `${DestinationOptionKind}`;
    checked: boolean;
    lastOfType?: boolean;
  }

  const props = withDefaults(defineProps<DestinationOptionProps>(), {
    lastOfType: false,
  });

  interface DestinationOptionEmits {
    (e: 'toggle'): void;
  }

  const emit = defineEmits<DestinationOptionEmits>();

  const VISA_SUCCESS_COLOR = 'var(--color-success-900)';
  const VISA_BRAND_COLOR = 'var(--color-brand-primary)';

  const hotelName = computed<HotelNameParts | null>(() =>
    props.kind === 'hotel' ? splitHotelName(props.option.name) : null,
  );

  const name = computed<string>(() => {
    if (props.kind === 'selectAll') {
      return DESTINATION_TEXTS.selectAll;
    }
    return hotelName.value?.title ?? props.option.name;
  });

  const comment = computed<string>(() => {
    if (props.kind === 'resort') {
      return (props.option as Resort).countryName;
    }
    if (props.kind === 'selectAll') {
      return props.option.name;
    }
    return hotelName.value?.direction ?? '';
  });

  const nameSeparator = computed<string>(() => (comment.value ? ',' : ''));

  const visa = computed(() => {
    if (props.kind === 'country') {
      return getVisaLabel(props.option as Country);
    }
    return null;
  });

  const visaColor = computed<string>(() => {
    return visa.value?.tone === 'success' ? VISA_SUCCESS_COLOR : VISA_BRAND_COLOR;
  });

  const price = computed<string>(() => {
    if (props.kind === 'country' || props.kind === 'resort') {
      return formatMinPrice((props.option as Country | Resort).minPrice);
    }
    return '';
  });

  const onToggle = () => {
    emit('toggle');
  };
</script>

<template>
  <div
    :class="['destination-option']"
    data-testid="destination-option"
    :data-id="option.id"
    :data-type="kind"
    @click.prevent="onToggle"
  >
    <Checkbox
      class="destination-option__checkbox"
      data-testid="destination-option-checkbox"
      :size="CheckboxSize.L"
      :model-value="checked"
      @update:modelValue="onToggle"
    />

    <span class="destination-option__body">
      <span class="destination-option__title">
        <Typo
          class="destination-option__name"
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ name }}{{ nameSeparator }}
        </Typo>

        <Typo
          v-if="comment"
          class="destination-option__comment"
          data-testid="destination-secondary"
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ comment }}
        </Typo>

        <Typo
          v-if="visa"
          class="destination-option__visa"
          data-testid="destination-visa"
          :variant="TypoVariant.BodyLRegular"
          :style="{ color: visaColor }"
        >
          {{ visa.text }}
        </Typo>
      </span>

      <Typo
        v-if="price"
        class="destination-option__price"
        data-testid="destination-min-price"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        {{ price }}
      </Typo>
    </span>
  </div>
</template>

<style scoped lang="scss">
  .destination-option {
    align-items: flex-start;
    background-color: var(--color-bg-primary);
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    gap: var(--spacing-xs);
    min-height: 40px;
    padding: var(--spacing-xs) var(--spacing-m);

    &:hover {
      background-color: var(--color-bg-secondary);
    }

    &__checkbox {
      flex-shrink: 0;
    }

    &__body {
      align-items: flex-start;
      display: flex;
      flex-grow: 1;
      gap: var(--spacing-s);
      justify-content: space-between;
      min-width: 0;
    }

    &__title {
      align-items: baseline;
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-xxs);
      min-width: 0;
    }

    &__name {
      color: var(--color-text-primary);
    }

    &__comment {
      color: var(--color-text-secondary);
    }

    &__visa {
      flex-shrink: 0;
    }

    &__price {
      color: var(--color-text-secondary);
      flex-shrink: 0;
      white-space: nowrap;
    }
  }
</style>
